// src/componentes/Layout/Sidebar.js
import React, { useEffect, useState } from 'react';
import {
    Box,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
} from '@mui/material';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import InventoryIcon from '@mui/icons-material/Inventory';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

const drawerWidth = 240;
const collapsedWidth = 64;

// 🔒 Mapa estático: título + icono + ruta
// Las rutas deben coincidir con las que tienes en la BD
const menuItems = [
    { title: 'Dashboard', path: '/inicio', icon: <DashboardIcon /> },
    { title: 'Usuarios', path: '/usuarios', icon: <PeopleIcon /> },
    { title: 'Productos', path: '/productos', icon: <InventoryIcon /> },
    { title: 'Mantenciones', path: '/mantenciones', icon: <HomeRepairServiceIcon /> },
    { title: 'Reparaciones', path: '/reparaciones', icon: <BuildIcon /> },
    { title: 'Configuración', path: '/configuracion', icon: <SettingsIcon /> }
];

const Sidebar = ({ isMobile, mobileOpen, onCloseMobile, desktopOpen }) => {
    const location = useLocation();

    // 🔒 Aquí guardamos los módulos permitidos por la API
    const [modulosPermitidos, setModulosPermitidos] = useState([]);

    // 📌 Cargar los módulos permitidos al iniciar
    useEffect(() => {
        const fetchModulos = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;

                const res = await axios.get(
                    process.env.REACT_APP_OBTENERMODULOS,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (res.data.success) {
                    setModulosPermitidos(res.data.modulos); // [{nombre, ruta}]
                }
            } catch (error) {
                console.error("Error cargando módulos permitidos:", error);
            }
        };

        fetchModulos();
    }, []);

    // 🔍 Filtrar menú según rutas permitidas
    const filteredMenu = menuItems.filter((item) =>
        modulosPermitidos.some((m) => m.ruta === item.path)
    );

    const drawerContent = (
        <Box sx={{ px: 1, pt: 2 }}>
            <Toolbar />
            {!isMobile && desktopOpen && (
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 600,
                        color: '#9e9e9e',
                        textTransform: 'uppercase',
                        mb: 1,
                        pl: 2,
                    }}
                >
                    Navegación
                </Typography>
            )}

            <List disablePadding>
                {filteredMenu.map((item) => {
                    const isActive = location.pathname === item.path;

                    return (
                        <ListItemButton
                            key={item.title}
                            component={Link}
                            to={item.path}
                            selected={isActive}
                            onClick={isMobile ? onCloseMobile : undefined}
                            sx={{
                                borderRadius: 1,
                                mx: 1,
                                my: 0.5,
                                color: isActive ? 'primary.main' : 'text.primary',
                                backgroundColor: isActive ? '#e3f2fd' : 'transparent',
                                justifyContent: desktopOpen ? 'flex-start' : 'center',
                                px: desktopOpen ? 2 : 0,
                                py: 1.5,
                                minHeight: 48,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    color: isActive ? 'primary.main' : 'inherit',
                                    minWidth: 0,
                                    mr: desktopOpen ? 2 : 0,
                                    justifyContent: 'center',
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>

                            {desktopOpen && (
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" fontWeight={isActive ? 600 : 500}>
                                            {item.title}
                                        </Typography>
                                    }
                                />
                            )}
                        </ListItemButton>
                    );
                })}
            </List>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onCloseMobile}
                ModalProps={{ keepMounted: true }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: desktopOpen ? drawerWidth : collapsedWidth,
                        transition: 'width 0.3s',
                        overflowX: 'hidden',
                        backgroundColor: '#ffffff',
                        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.05)',
                        borderRight: 'none',
                    },
                }}
            >
                {drawerContent}
            </Drawer>
        );
    }

    return (
        <Drawer
            variant="permanent"
            open
            sx={{
                width: desktopOpen ? drawerWidth : collapsedWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: desktopOpen ? drawerWidth : collapsedWidth,
                    transition: 'width 0.3s',
                    overflowX: 'hidden',
                    backgroundColor: '#f9fafb',
                },
            }}
        >
            {drawerContent}
        </Drawer>
    );
};

export default Sidebar;
